// Pre-carga del cache para trabajar offline.
//
// Al login y al switch-establishment, llena el cache de TanStack con los
// catalogos y el snapshot del establecimiento (las dos puertas de bajada del
// backend, Fase 2). Asi, cuando el operario despues pierde señal, las listas ya
// estan en el telefono.
//
// Best-effort: si falla (ej. primer login sin red), no rompe el flujo de login.
import { queryClient } from "./queryClient";
import { queryKeys } from "./keys";
import { fetchCatalogs, fetchSnapshot } from "$lib/java/sync/syncback";
import { getEstablishmentId } from "$lib/java/establecimientos/establecimientosback";
import { seedFromSnapshot, seedFromCatalogs } from "./seedFromSnapshot";
import { getUser } from "$lib/userstorage/usersotrage";
import { getScopedToken } from "$lib/java/auth/refreshSession";
import { loadStorageEstablecimiento } from "$lib/java/establecimientos/establecimientostorage";
import { offlineQueriesFor } from "./offlineQueries";
import { getUnsynced } from "$lib/outbox/outboxApi";
import { isOnline } from "$lib/sync/networkStatus";

// Cuántas queries de precarga se piden a la vez. Bajo a propósito: corre en
// background mientras el usuario usa la app y no debe competir con sus fetchs.
const PREFETCH_CONCURRENCY = 3;

// Último establecimiento precargado. Al cambiar de establecimiento hay que
// refrescar de verdad (ignorando el staleTime): el usuario acaba de pedir ver
// otro tenant y esperar 5 minutos a que venza el cache no es aceptable. Se
// detecta acá y no en los call sites para cubrir todos los caminos de switch
// (navbar, pantalla de establecimientos, login) sin tocarlos uno por uno.
let ultimoEstPrecargado = null;

/** ¿`a` es prefijo de `b`? Compara elemento a elemento en forma laxa. */
function esPrefijo(a, b) {
    return a.length <= b.length && a.every((v, i) => String(v) === String(b[i]));
}

/**
 * Keys que NO hay que refetchear porque tienen escrituras sin confirmar: traer
 * la lista del server borraría las filas optimistas de la pantalla.
 *
 * Se compara por PREFIJO en ambos sentidos, no por igualdad: un alta de animal
 * encola la key base `["animals", estId]` pero además inserta la fila optimista
 * en `["animals", estId, "all"]`, así que bloquear sólo la key exacta dejaría
 * que el refetch de "all" se comiera el alta.
 *
 * Devuelve null si alguna operación no registró su queryKey: en ese caso no se
 * puede saber qué protege y no se precarga nada (conservador).
 */
function keysBloqueadas(filas) {
    const bloqueadas = [];
    for (const fila of filas) {
        if (!Array.isArray(fila.queryKey)) return null;
        bloqueadas.push(fila.queryKey);
    }
    return bloqueadas;
}

/**
 * Establecimiento al que está scopeado el access token de la sesión (claim
 * `establishmentId`). Es la única fuente confiable para saber a qué tenant le va
 * a pegar una queryFn: al cambiar de establecimiento el token se actualiza ANTES
 * que el storage del establecimiento activo, así que comparar contra el storage
 * daría falsos negativos (o peor, cachearía datos del tenant viejo bajo la key
 * del nuevo). Devuelve null si no se puede leer → no se precarga nada.
 */
function tokenEstablishmentId() {
    const token = getUser()?.token;
    const payload = token?.split(".")?.[1];
    if (!payload) return null;
    try {
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(json)?.establishmentId ?? null;
    } catch (e) {
        return null;
    }
}

/**
 * Precarga en background TODAS las listas que usan las pantallas, para no tener
 * que entrar online a cada una para cachearla. Best-effort: cada query falla por
 * su cuenta y ninguna corta a las demás.
 *
 * Sólo corre para el establecimiento al que apunta el token de sesión, porque las
 * queryFn de las pantallas no aceptan un token scoped (ver offlineQueries.js).
 *
 * Con escrituras sin sincronizar se saltean SÓLO las listas involucradas (traer
 * esa lista del server borraría sus filas optimistas); las demás se precargan
 * igual, así una observación pendiente no deja sin cachear pesajes o movimientos.
 *
 * Al cambiar de establecimiento refetchea ignorando el staleTime.
 */
export async function prefetchOfflineQueries(estId, { force = false } = {}) {
    if (!estId) return;
    if (!isOnline()) return;
    if (String(tokenEstablishmentId() ?? "") !== String(estId)) return;

    let bloqueadas;
    try {
        bloqueadas = keysBloqueadas(await getUnsynced());
    } catch (e) {
        // Si no se puede leer la outbox, no arriesgamos pisar nada.
        return;
    }
    if (bloqueadas === null) {
        console.warn("[sync] hay operaciones sin queryKey: se omite la precarga");
        return;
    }

    const cambioDeEstablecimiento =
        ultimoEstPrecargado !== null && String(ultimoEstPrecargado) !== String(estId);
    ultimoEstPrecargado = estId;
    const refrescar = force || cambioDeEstablecimiento;

    const queue = offlineQueriesFor(estId).filter((q) => {
        const protegida = bloqueadas.some(
            (b) => esPrefijo(b, q.queryKey) || esPrefijo(q.queryKey, b),
        );
        if (protegida) {
            console.info(`[sync] ${q.label} tiene cambios sin sincronizar: no se refetchea`);
        }
        return !protegida;
    });

    async function worker() {
        while (queue.length) {
            const q = queue.shift();
            try {
                await queryClient.prefetchQuery({
                    queryKey: q.queryKey,
                    queryFn: q.queryFn,
                    // Por defecto respeta el staleTime de 5 min, así que las keys
                    // que el snapshot acaba de sembrar no se vuelven a pedir. Al
                    // cambiar de establecimiento se fuerza el fetch.
                    ...(refrescar ? { staleTime: 0 } : {}),
                });
            } catch (err) {
                console.warn(`[sync] prefetch de ${q.label} falló:`, err);
            }
        }
    }
    await Promise.all(
        Array.from({ length: Math.min(PREFETCH_CONCURRENCY, queue.length) }, worker),
    );
}

// `token` opcional: scoped a `estId`. Sin token usa el de la sesión activa
// (para el establecimiento activo). Con token, pre-cachea un establecimiento
// que no es el activo (multi-establecimiento).
export async function prefetchEstablishmentData(estId, token) {
    if (!estId) return;
    try {
        await Promise.all([
            // Catalogos: ademas de cachear el blob, sembrar las keys de los
            // selectores (tipos de tratamiento, lotes, rodeos) para poder CREAR
            // eventos offline en cold-start.
            (async () => {
                const catalogs = await fetchCatalogs(token);
                queryClient.setQueryData(queryKeys.catalogs(estId), catalogs);
                seedFromCatalogs(estId, catalogs);
            })(),
            // Snapshot: ademas de cachear el blob, sembrar las listas por entidad
            // para que funcionen offline en cold-start (lectura).
            (async () => {
                const snapshot = await fetchSnapshot(token);
                queryClient.setQueryData(queryKeys.snapshot(estId), snapshot);
                seedFromSnapshot(estId, snapshot);
            })(),
            // Detalle del establecimiento (renspa, provincia, contacto, coords…):
            // lo consume /establecimiento offline. Se cachea por establecimiento
            // para que funcione aun sin haber abierto la pantalla (cold-start).
            (async () => {
                try {
                    const record = await getEstablishmentId(estId, token);
                    queryClient.setQueryData(queryKeys.establishment(estId), record);
                } catch (e) {
                    // No abortar el seed de snapshot/catalogs por el detalle.
                    console.warn("[sync] prefetch detalle establecimiento fallo:", e);
                }
            })(),
        ]);
    } catch (err) {
        // No bloquear el login/switch si no hay red o el backend falla.
        console.warn("[sync] prefetchEstablishmentData fallo:", err);
    }

    // Completa lo que el snapshot no cubre (la lista de animales con inactivos,
    // pesajes, movimientos, stock) pidiendo cada lista con la queryFn real de su
    // pantalla. Deliberadamente SIN await: el login espera sólo el snapshot, como
    // hasta ahora, y el resto termina de cargarse en background. La propia función
    // decide si corresponde para este establecimiento.
    prefetchOfflineQueries(estId).catch(() => {});
}

// Pre-cachea los datos de TODOS los establecimientos del usuario, para poder
// trabajar offline en cualquiera sin haberlo visitado antes. El activo usa el
// token de sesión; los demás, un access scoped canjeado del refresh. Best-effort
// por establecimiento, con concurrencia acotada. Se dispara al login y en la
// revalidación oportunista (al abrir/reconectar). Multi-establecimiento.
export async function prefetchAllEstablishments() {
    if (!isOnline()) return;
    const ests = getUser()?.establishments || [];
    if (!ests.length) return;
    const activeEstId = loadStorageEstablecimiento()?.id ?? null;

    const queue = [...ests];
    const CONCURRENCY = 3;
    async function worker() {
        while (queue.length) {
            const est = queue.shift();
            const estId = est?.establishmentId;
            if (!estId) continue;
            const isActive = String(estId) === String(activeEstId);
            let token;
            if (isActive) {
                token = undefined; // usa el token de la sesión activa
            } else {
                const r = await getScopedToken(estId);
                if (r.reason !== "ok") continue; // sin red / sin acceso a ese est
                token = r.token;
            }
            await prefetchEstablishmentData(estId, token);
        }
    }
    await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker),
    );
}
